/**
 * displayGameTitle — clean Overwolf's internal title artifacts before showing a
 * game name to a user.
 *
 * Overwolf attributes some sessions to catch-all or launcher processes and
 * sends its own label through as `gameTitle`, which we store verbatim on
 * OWTimeLog and surface in notifications, the status chip and the last-session
 * card. Measured in production 2026-08-21:
 *
 *     Call of Duty General Instance   12,130 logs   35 users
 *     LOL Launcher                     1,025 logs   25 users
 *     Minecraft Launcher                 242 logs    1 user
 *     League of Legends Launcher         166 logs    8 users
 *     Rockstar Launcher                   28 logs    1 user
 *
 * So a player currently sees "Call of Duty General Instance is now running."
 * "General Instance" is Overwolf's bucket for a Call of Duty title it cannot
 * pin down — Black Ops 7 sessions almost certainly land there, since BO7 has no
 * record anywhere in our data and raw_data carries no build identifier to
 * separate them (see the game-intake task).
 *
 * DELIBERATELY CONSERVATIVE. This only strips suffixes that are demonstrably
 * Overwolf's own vocabulary, and never renames one game to another. It does NOT
 * map "LOL Launcher" to "League of Legends" — that needs a real identifier
 * mapping, and guessing at names is how the old static process map ended up
 * claiming VALORANT was Apex Legends.
 *
 * Variant markers that describe a REAL distinct client — PBE, Beta — are left
 * alone. "League of Legends PBE" is not "League of Legends".
 */

/*
 * Ordered: strip the catch-all marker before the launcher marker, so
 * "<Game> General Instance" and "<Game> Launcher" both reduce to "<Game>"
 * without one rule masking the other.
 */
const TITLE_ARTIFACTS = [
    /\s+General\s+Instance$/i,
    /\s+Launcher$/i,
];

export function displayGameTitle(title: string | undefined | null): string {
    if (!title) {
        return "";
    }

    let cleaned = String(title).trim();
    for (const artifact of TITLE_ARTIFACTS) {
        cleaned = cleaned.replace(artifact, "").trim();
    }

    /*
     * Never return an empty string — a title that is ONLY an artifact
     * ("Launcher") is better shown as-is than blanked, since the caller
     * renders it into a sentence.
     */
    return cleaned || String(title).trim();
}
